// library imports
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

// store imports
import { useDomainStore, useFamilyStore } from '@/hooks/useStores';

// component imports
import Spinner from '@/components/shared/Spinner';
import InviteFamilyMemberForm from '@/components/invitations/InviteFamilyMemberForm';
import InvitationsList from '@/components/invitations/InvitationsList';
import CollaboratorsRoleList from '@/components/invitations/CollaboratorsRoleList';

// utils
import { canInvite } from '@/utils/rolePermissions';
import { getFamilyPlanUpgradeHint } from '@/utils/familyPlan';

// styles
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import './invite-family-members-screen.css';

const InviteFamilyMembersScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tNav } = useTranslation('nav');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const { familyId: familyIdParam } = useParams<{
    familyId: string;
  }>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState<number>(0);

  useEffect(() => {
    const loadFamilyData = async () => {
      setErrorMessage(null);

      if (!familyIdParam) {
        setErrorMessage(t('errors.noFamily'));
        return;
      }

      try {
        domainStore.setSelectedFamily(familyIdParam);
        await familyStore.loadFamilyData(familyIdParam);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : t('inviteMembers.loadFailedFallback');
        setErrorMessage(errorMsg);
        // eslint-disable-next-line no-console
        console.error('Error loading family data:', error);
      }
    };

    loadFamilyData();
  }, [domainStore, familyStore, familyIdParam, t]);

  if (errorMessage) {
    return (
      <div className="invite-page invite-page--centered">
        <section className="invite-page__section invite-page__section--message">
          <h2 className="invite-page__section-title">{t('inviteMembers.loadFailedTitle')}</h2>
          <p className="invite-page__body">{errorMessage}</p>
          <Link to="/families" className="btn btn--primary">
            {tNav('myFamilies')}
          </Link>
        </section>
      </div>
    );
  }

  const isLoading = !familyStore.isLoaded && familyStore.isLoading;

  if (isLoading) {
    return (
      <div className="invite-page">
        <div className="invite-page__loading">
          <Spinner message={t('overview.loading')} />
        </div>
      </div>
    );
  }

  if (!familyIdParam) {
    return (
      <div className="invite-page invite-page--centered">
        <section className="invite-page__section invite-page__section--message">
          <h2 className="invite-page__section-title">{t('inviteMembers.noFamilyTitle')}</h2>
          <p className="invite-page__body">{t('inviteMembers.noFamilyBody')}</p>
          <Link to="/families" className="btn btn--primary">
            {tNav('myFamilies')}
          </Link>
        </section>
      </div>
    );
  }

  const currentFamily = domainStore.currentFamily;
  const userRole = currentFamily?.userRole;
  const familyPlan = currentFamily?.plan;
  const hasInvitePermission = canInvite(userRole, familyPlan);

  if (!hasInvitePermission) {
    const isOwner = userRole === 'owner';
    const upgradeHint = getFamilyPlanUpgradeHint(isOwner);
    return (
      <div className="invite-page invite-page--centered">
        <section className="invite-page__section invite-page__section--message">
          <h2 className="invite-page__section-title">{t('inviteMembers.accessDeniedTitle')}</h2>
          <p className="invite-page__body">{t('inviteMembers.accessDeniedBody', { upgradeHint })}</p>
          <Link to={`/families/${familyIdParam}/overview`} className="btn btn--primary">
            {t('inviteMembers.backToOverview')}
          </Link>
        </section>
      </div>
    );
  }

  return (
    <div className="invite-page">
      <header className="invite-page__header">
        <h1 className="invite-page__title">{t('inviteMembers.title')}</h1>
        <p className="invite-page__lede">
          {t('inviteMembers.lede')} <ScreenHelpDot />
        </p>
      </header>

      <section className="invite-page__section">
        <InviteFamilyMemberForm
          familyId={familyIdParam}
          onSuccess={() => {
            setInvitationsRefreshKey((prev) => prev + 1);
          }}
        />
      </section>

      <section className="invite-page__section">
        <InvitationsList
          key={invitationsRefreshKey}
          familyId={familyIdParam}
          onRefresh={() => {
            setInvitationsRefreshKey((prev) => prev + 1);
          }}
        />
      </section>

      <section className="invite-page__section">
        <CollaboratorsRoleList familyId={familyIdParam} />
      </section>
    </div>
  );
});

export default InviteFamilyMembersScreen;
